import React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { RangeInput } from 'ui/standard/input/range_input';

import { Host } from 'services/pangolin/types';
import { hostDisplayDeviceType, hostDisplayName } from 'services/pangolin/hosts_service';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import { DeviceBandwidthStore } from './device_bandwidth_store';

import Messages from './device_bandwidth_messages';
import baseStyles from './device_bandwidth.module.css';

type DeviceBandwidthStyles = {
  container: string;
  row: string;
  deviceName: string;
  column: string;
  labelRow: string;
  labelText: string;
  deviceIcon: string;
};

const themedStyles: ThemedStyles<DeviceBandwidthStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.daylightContainer,
    row: baseStyles.baseRow,
    deviceName: baseStyles.baseDeviceName,
    column: baseStyles.baseColumn,
    labelRow: baseStyles.baseLabelRow,
    labelText: baseStyles.daylightLabelText,
    deviceIcon: baseStyles.deviceIcon,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.midnightContainer,
    row: baseStyles.baseRow,
    deviceName: baseStyles.baseDeviceName,
    column: baseStyles.baseColumn,
    labelRow: baseStyles.baseLabelRow,
    labelText: baseStyles.midnightLabelText,
    deviceIcon: baseStyles.deviceIcon,
  },
};

type DeviceBandwidthProps = {
  host: Host;
  store: DeviceBandwidthStore;
};

export const DeviceBandwidth = observer(({ host, store }: DeviceBandwidthProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = getDeviceTypeIcon(deviceIcons, hostDisplayDeviceType(host));
  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div>
          <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
        </div>
        <div className={styles.deviceName}>
          {hostDisplayName(host)}
        </div>
      </div>
      <div className={styles.column}>
        <div className={styles.labelRow}>
          <div className={styles.labelText}>
            {Messages.downloadSpeed()}
          </div>
          <div className={styles.labelText}>
            {numberToPercentage(store.download.value)}
          </div>
        </div>
        <RangeInput store={store.download} min={0} max={1} step={0.01}/>
        <div className={styles.labelRow}>
          <div className={styles.labelText}>
            {Messages.uploadSpeed()}
          </div>
          <div className={styles.labelText}>
            {numberToPercentage(store.upload.value)}
          </div>
        </div>
        <RangeInput store={store.upload} min={0} max={1} step={0.01}/>
      </div>
    </div>
  );
});

function numberToPercentage(n: number): string {
  return `${Math.round(n * 100)}%`;
}
